import { AsyncButton } from "aic";
import { IEditorV3 } from "editor-v3";
import React, { ReactNode } from "react";
import { Floppy, PenFill } from "react-bootstrap-icons";
import { CommentSaveButtonStatus } from "./CommentBox";
import { CommentViewContext } from "./interface";
import { useComments } from "./useComments";

interface UpdateCommentButtonProps {
  activeContext: CommentViewContext;
  currentStatus: string;
  disabled: boolean;
  commentId: string;
  currentContent: IEditorV3;
  setCurrentStatus: (status: CommentSaveButtonStatus) => void;
}

/**
 * Button to post or edit a comment.
 *
 * @param props - Component props.
 * @param props.activeContext - The active comment view context.
 * @param props.currentStatus - Current save status: 'new', 'editing' or 'saved'.
 * @param props.disabled - Whether the button is disabled.
 * @param props.commentId - ID of the comment to update.
 * @param props.currentContent - Editor content for the comment.
 * @param props.setCurrentStatus - Setter for the comment save status.
 */
export const UpdateCommentButton = ({
  activeContext,
  currentStatus,
  disabled,
  commentId,
  currentContent,
  setCurrentStatus,
}: UpdateCommentButtonProps): ReactNode => {
  const { updateComment, updateRequest } = useComments(activeContext);

  /**
   * Handle click event to post or edit the comment.
   *
   * @param e - Mouse event
   */
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    switch (currentStatus) {
      case "new":
      case "editing": {
        await updateComment(commentId, currentContent);
        setCurrentStatus("saved");
        break;
      }
      case "saved":
        setCurrentStatus("editing");
        break;
    }
  };

  return (
    <AsyncButton
      size="sm"
      aria-label={["new", "editing"].includes(currentStatus) ? "Post comment" : "Edit comment"}
      disabled={disabled}
      id={`update-comment-${commentId}`}
      onClick={handleClick}
      variant={currentStatus === "editing" ? "primary" : "outline-primary"}
      requestStatus={updateRequest}
    >
      {["new", "editing"].includes(currentStatus) ? <Floppy size={14} /> : <PenFill size={14} />}
    </AsyncButton>
  );
};

UpdateCommentButton.displayName = "UpdateCommentButton";
